import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { authService } from './authService';
import { firebaseAuthService } from './firebaseAuthService';
import { AuthContextType, Customer, LoginCredentials, FirebaseUser } from './types';
import { getProfile } from '../api/profile';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const STORAGE_KEYS = {
  USER: 'auth_user',
  TOKENS: 'auth_tokens',
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<Customer | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);

  const isAuthenticated = !!firebaseUser && !!user; // Require both Firebase auth AND profile data

  const clearAuth = useCallback(() => {
    console.log('Clearing all authentication data');
    setUser(null);
    setFirebaseUser(null);
    setIsProfileLoaded(false);
    authService.removeAuthToken();
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.TOKENS);
  }, []);

  const initializeAuth = useCallback(async () => {
    try {
      // Wait a bit for Firebase to fully initialize and restore auth state
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log('🔍 Checking for existing Firebase user...');
      // Check if there's already a Firebase user (for page reloads)
      const currentFirebaseUser = firebaseAuthService.getCurrentUser();
      console.log('🔍 Firebase user check result:', currentFirebaseUser ? `Found: ${currentFirebaseUser.email}` : 'None found');
      if (currentFirebaseUser) {
        console.log('🔥 Found existing Firebase user on page reload:', currentFirebaseUser.email);
        
        const firebaseUserData: FirebaseUser = {
          uid: currentFirebaseUser.uid,
          email: currentFirebaseUser.email,
          emailVerified: currentFirebaseUser.emailVerified,
          displayName: currentFirebaseUser.displayName,
          photoURL: currentFirebaseUser.photoURL,
        };
        setFirebaseUser(firebaseUserData);
        
        // Try to fetch profile data for existing user
        try {
          const profileResponse = await getProfile();
          setUser(profileResponse.customer);
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(profileResponse.customer));
          setIsProfileLoaded(true);
        } catch (profileError) {
          console.error('Error fetching profile for existing user:', profileError);
          // If profile fetch fails, try to restore from localStorage as fallback
          const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              setUser(parsedUser);
              setIsProfileLoaded(true);
              console.log('✅ Profile restored from localStorage as fallback');
            } catch (parseError) {
              console.error('Error parsing stored user data:', parseError);
              setUser(null);
              setIsProfileLoaded(false);
              localStorage.removeItem(STORAGE_KEYS.USER);
            }
          } else {
            setUser(null);
            setIsProfileLoaded(false);
          }
        }
      }

      // Initialize Firebase auth state listener
      console.log('Setting up Firebase auth state listener...');
      const unsubscribe = await firebaseAuthService.onAuthStateChanged(async (firebaseUser) => {
        
        if (firebaseUser) {
          const firebaseUserData: FirebaseUser = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            emailVerified: firebaseUser.emailVerified,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
          };
          setFirebaseUser(firebaseUserData);
          
          // Always fetch fresh profile data when Firebase user is authenticated
          // This ensures we have the most up-to-date user information
          try {
            console.log('Fetching fresh profile data for Firebase user:', firebaseUser.email);
            const profileResponse = await getProfile();
            setUser(profileResponse.customer);
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(profileResponse.customer));
            setIsProfileLoaded(true);
            console.log('Profile data loaded and stored successfully:', profileResponse.customer.email);
          } catch (profileError) {
            console.error('Error fetching profile after Firebase auth:', profileError);
            // If profile fetch fails, try to restore from localStorage as fallback
            const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
            if (storedUser) {
              try {
                const parsedUser = JSON.parse(storedUser);
                setUser(parsedUser);
                setIsProfileLoaded(true);
                console.log('✅ Profile restored from localStorage after Firebase auth');
              } catch (parseError) {
                console.error('Error parsing stored user data:', parseError);
                setUser(null);
                setIsProfileLoaded(false);
                localStorage.removeItem(STORAGE_KEYS.USER);
              }
            } else {
              setUser(null);
              setIsProfileLoaded(false);
            }
          }
        } else {
          console.log('Firebase user signed out, clearing auth state only');
          setFirebaseUser(null);
          setUser(null);
          setIsProfileLoaded(false);
          // Don't clear localStorage here - only clear on explicit logout
        }
      });

      // If no Firebase user was found, try to restore from localStorage as a fallback
      // This helps with cases where Firebase auth state hasn't been restored yet
      if (!currentFirebaseUser) {
        const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            setIsProfileLoaded(true);
            console.log('✅ Profile restored from localStorage while waiting for Firebase auth');
          } catch (parseError) {
            console.error('Error parsing stored user data:', parseError);
            localStorage.removeItem(STORAGE_KEYS.USER);
          }
        }
      }
      
      // Note: We don't set stored user data here anymore
      // The Firebase auth state listener will handle profile loading
      // This prevents race conditions and ensures data consistency
      console.log('Auth initialization complete, waiting for Firebase auth state...');

      // Return cleanup function
      return unsubscribe;
    } catch (error) {
      console.error('Error initializing auth:', error);
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  }, [clearAuth]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    
    const init = async () => {
      unsubscribe = await initializeAuth();
    };
    
    init();
    
    // Cleanup Firebase listener on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [initializeAuth]);


  const login = async (credentials: LoginCredentials) => {
    try {
      setIsLoading(true);
      
      // Sign in with Firebase - the auth state listener will handle profile loading
      await firebaseAuthService.signIn(credentials.email, credentials.password);
      
      // Get the Firebase user to check if sign-in was successful
      const firebaseUser = firebaseAuthService.getCurrentUser();
      if (!firebaseUser) {
        throw new Error('Firebase authentication failed');
      }
      
      // Force profile refresh after successful login to ensure we have the latest data
      // This is especially important after registration when the user might already be logged in
      try {
        console.log('Force refreshing profile after login for:', firebaseUser.email);
        const profileResponse = await getProfile();
        setUser(profileResponse.customer);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(profileResponse.customer));
        setIsProfileLoaded(true);
        console.log('Profile refreshed successfully after login:', profileResponse.customer.email);
      } catch (profileError) {
        console.error('Error refreshing profile after login:', profileError);
        // If profile fetch fails, clear the user state
        setUser(null);
        setIsProfileLoaded(false);
        localStorage.removeItem(STORAGE_KEYS.USER);
        throw new Error('Failed to load user profile after login');
      }
      
      // Note: Profile loading will also be handled by the Firebase auth state change listener
      // This ensures we have the most up-to-date user information
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      setIsLoading(true);
      
      // Sign in with Google via Firebase - the auth state listener will handle profile loading
      await firebaseAuthService.signInWithGoogle();
      
      // Get the Firebase user to check if sign-in was successful
      const firebaseUser = firebaseAuthService.getCurrentUser();
      if (!firebaseUser) {
        throw new Error('Google authentication failed');
      }
      
      // Note: Profile loading will be handled by the Firebase auth state change listener
      // This prevents duplicate API calls and race conditions
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Sign out from Firebase
      await firebaseAuthService.signOut();
      // Note: Backend logout is not needed since we're using Firebase auth
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
    }
  };

  const refreshProfile = async () => {
    try {
      if (!firebaseUser) {
        throw new Error('No Firebase user to refresh profile for');
      }
      
      console.log('Refreshing profile data for:', firebaseUser.email);
      const profileResponse = await getProfile();
      setUser(profileResponse.customer);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(profileResponse.customer));
      setIsProfileLoaded(true);
      console.log('Profile refreshed successfully:', profileResponse.customer.email);
    } catch (error) {
      console.error('Error refreshing profile:', error);
      // If refresh fails, clear the user state
      setUser(null);
      setIsProfileLoaded(false);
      localStorage.removeItem(STORAGE_KEYS.USER);
      throw error;
    }
  };

  const refreshToken = async () => {
    try {
      // Since we're using Firebase auth, we don't need to refresh backend tokens
      // Firebase handles token refresh automatically
      console.log('Firebase handles token refresh automatically');
    } catch (error) {
      console.error('Token refresh error:', error);
      clearAuth();
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    firebaseUser,
    isAuthenticated,
    isLoading,
    isProfileLoaded,
    login,
    loginWithGoogle,
    logout,
    refreshToken,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 